import React, { useState } from "react";
import { useSubscription } from "../../hooks/useSubscription";
import { ProBadge, UpgradeTooltip } from "./UpgradePrompt";

export interface ScheduleConfig {
  enabled: boolean;
  cron: string;
  timezone: string;
  frequency: "daily" | "weekly" | "monthly" | "quarterly";
  days: string[]; // Days of week for daily, or day of month for monthly/quarterly
  time: string; // HH:MM format;
}

interface ScheduleSelectorProps {
  value: ScheduleConfig;
  onChange: (config: ScheduleConfig) => void;
  className?: string;
  selectedDate?: Date | null;
}

const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({
  value,
  onChange,
  className = "",
  selectedDate,
}) => {
  const [localConfig, setLocalConfig] = useState<ScheduleConfig>(value);
  const { features, isPro } = useSubscription();

  // Days of the week
  const daysOfWeek = [
    { key: "monday", label: "Mon", value: "1" },
    { key: "tuesday", label: "Tue", value: "2" },
    { key: "wednesday", label: "Wed", value: "3" },
    { key: "thursday", label: "Thu", value: "4" },
    { key: "friday", label: "Fri", value: "5" },
    { key: "saturday", label: "Sat", value: "6" },
    { key: "sunday", label: "Sun", value: "0" },
  ];

  // Common timezones
  const timezones = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "UTC", label: "UTC" },
    { value: "Europe/London", label: "London (GMT)" },
    { value: "Europe/Paris", label: "Paris (CET)" },
    { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  ];

  // Generate cron expression based on current config
  const generateCronExpression = (config: ScheduleConfig): string => {
    const [hour, minute] = config.time.split(":").map(Number);

    switch (config.frequency) {
      case "daily":
        // For daily: run on selected days of week at specified time
        if (config.days.length === 0) return `${minute} ${hour} * * *`; // Every day
        if (config.days.length === 7) return `${minute} ${hour} * * *`; // Every day
        return `${minute} ${hour} * * ${config.days.join(",")}`;

      case "weekly": {
        // For weekly: run on selected days of week at specified time
        if (config.days.length === 0) return `${minute} ${hour} * * *`; // Every day
        if (config.days.length === 7) return `${minute} ${hour} * * *`; // Every day
        return `${minute} ${hour} * * ${config.days.join(",")}`;
      }

      case "monthly": {
        // For monthly: run on first occurrence of selected day each month
        if (config.days.length === 0) return `${minute} ${hour} 1 * *`; // First day of month
        // Use first selected day, convert to day of month (1-28 to be safe)
        const dayOfMonth = Math.min(parseInt(config.days[0]) || 1, 28);
        return `${minute} ${hour} ${dayOfMonth} * *`;
      }

      case "quarterly": {
        // For quarterly: run on first occurrence of selected day in Jan, Apr, Jul, Oct
        if (config.days.length === 0) return `${minute} ${hour} 1 1,4,7,10 *`; // First day of quarter
        const qDayOfMonth = Math.min(parseInt(config.days[0]) || 1, 28);
        return `${minute} ${hour} ${qDayOfMonth} 1,4,7,10 *`;
      }

      default:
        return `${minute} ${hour} * * *`;
    }
  };

  // Helper to update config and notify parent
  const updateConfig = (updates: Partial<ScheduleConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    const newCron = generateCronExpression(newConfig);
    const finalConfig = { ...newConfig, cron: newCron };
    setLocalConfig(finalConfig);
    onChange(finalConfig);
  };

  const handleFrequencyChange = (
    frequency: "daily" | "weekly" | "monthly" | "quarterly"
  ) => {
    // Reset days when frequency changes
    const newDays = frequency === "daily" ? ["1"] : ["1"]; // Default to Monday for daily, 1st for monthly/quarterly
    updateConfig({ frequency, days: newDays });
  };

  const handleDayToggle = (dayValue: string) => {
    if (localConfig.frequency === "daily") {
      // For daily, allow multiple days
      const newDays = localConfig.days.includes(dayValue)
        ? localConfig.days.filter((d) => d !== dayValue)
        : [...localConfig.days, dayValue];
      updateConfig({ days: newDays });
    } else {
      // For monthly/quarterly, single day selection (treat as day of month)
      updateConfig({ days: [dayValue] });
    }
  };

  const handleDayOfMonthChange = (day: string) => {
    updateConfig({ days: [day] });
  };

  const renderDaySelector = () => {
    if (localConfig.frequency === "daily") {
      return (
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Days of Week
          </label>
          <div className="flex flex-wrap gap-2">
            {daysOfWeek.map((day) => (
              <button
                key={day.key}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                className={`px-3 py-2 text-xs font-medium border transition-colors ${
                  localConfig.days.includes(day.value)
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-black text-gray-300 border-gray-600 hover:bg-gray-800"
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Select which days of the week to run
          </p>
        </div>
      );
    } else if (localConfig.frequency === "weekly") {
      return (
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Day of Week
          </label>
          <div className="flex flex-wrap gap-2">
            {daysOfWeek.map((day) => (
              <button
                key={day.key}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                className={`px-3 py-2 text-xs font-medium border transition-colors ${
                  localConfig.days.includes(day.value)
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-black text-gray-300 border-gray-600 hover:bg-gray-800"
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Select which days of the week to run
          </p>
        </div>
      );
    } else {
      // For monthly/quarterly, select day of month
      return (
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Day of Month
          </label>
          <select
            value={localConfig.days[0] || "1"}
            onChange={(e) => handleDayOfMonthChange(e.target.value)}
            className="block w-full border border-white px-3 py-2 bg-black text-white focus:ring-purple-500 focus:border-purple-500 text-sm"
          >
            {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
              <option key={day} value={day.toString()}>
                {day}
                {day === 1 ? "st" : day === 2 ? "nd" : day === 3 ? "rd" : "th"}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-400">
            {localConfig.frequency === "monthly"
              ? "Day of each month to run"
              : "Day of the first month in each quarter (Jan, Apr, Jul, Oct)"}
          </p>
        </div>
      );
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Selected Date Display (if provided) */}
      {selectedDate && (
        <div className="bg-purple-900 bg-opacity-20 border border-purple-500 p-4">
          <div className="flex items-center space-x-2 mb-2">
            <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h4 className="text-sm font-medium text-purple-300">SELECTED DATE</h4>
          </div>
          <p className="text-lg font-semibold text-white">
            {selectedDate.toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="flex items-center space-x-3">
        <input
          id="enable-schedule"
          type="checkbox"
          checked={localConfig.enabled}
          onChange={(e) => updateConfig({ enabled: e.target.checked })}
          className="border-white text-purple-600 focus:ring-purple-500"
        />
        <label
          htmlFor="enable-schedule"
          className="text-sm font-medium text-white"
        >
          Enable automatic scheduling
        </label>
      </div>

      {localConfig.enabled && (
        <div className="space-y-4 p-4 bg-gray-900 border border-gray-700">
          {/* Frequency Selection */}
          <div>
            <label className="block text-sm font-medium text-white mb-3">
              Frequency
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: "daily", label: "ONE-TIME", requiresPro: false },
                { value: "weekly", label: "WEEKLY", requiresPro: true },
                { value: "monthly", label: "MONTHLY", requiresPro: true },
                { value: "quarterly", label: "QUARTERLY", requiresPro: true },
              ].map((freq) => {
                const isLocked = freq.requiresPro && !isPro;
                const isSelected = localConfig.frequency === freq.value;

                return (
                  <UpgradeTooltip
                    key={freq.value}
                    featureName={`${freq.label} scheduling`}
                  >
                    <div
                      className={`relative ${
                        isLocked ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <label
                        className={`flex items-center justify-between p-3 border-2 transition-all ${
                          isSelected && !isLocked
                            ? "border-purple-500 bg-purple-900 bg-opacity-30"
                            : isLocked
                            ? "border-gray-600 bg-gray-800 opacity-60"
                            : "border-gray-600 bg-black hover:border-gray-500"
                        } ${isLocked ? "pointer-events-none" : ""}`}
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <input
                            type="radio"
                            name="frequency"
                            value={freq.value}
                            checked={isSelected}
                            disabled={isLocked}
                            onChange={(e) =>
                              handleFrequencyChange(e.target.value as any)
                            }
                            className="border-white text-purple-600 focus:ring-purple-500 disabled:opacity-50"
                          />
                          <span
                            className={`text-sm font-medium ${
                              isLocked
                                ? "text-gray-500"
                                : isSelected
                                ? "text-white"
                                : "text-gray-300"
                            }`}
                          >
                            {freq.label}
                          </span>
                        </div>
                        {freq.requiresPro && <ProBadge className="ml-2" />}
                      </label>
                    </div>
                  </UpgradeTooltip>
                );
              })}
            </div>
            {!isPro && (
              <p className="mt-2 text-xs text-gray-400">
                💡 Upgrade to Pro to unlock weekly and monthly scheduling
              </p>
            )}
          </div>

          {/* Day Selection */}
          {renderDaySelector()}

          {/* Time Selection */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Time
            </label>
            <input
              type="time"
              value={localConfig.time}
              onChange={(e) => updateConfig({ time: e.target.value })}
              className="block border border-white px-3 py-2 bg-black text-white focus:ring-purple-500 focus:border-purple-500 text-sm"
            />
            <p className="mt-1 text-xs text-gray-400">
              Time to execute the workflow
            </p>
          </div>

          {/* Timezone Selection */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Timezone
            </label>
            <select
              value={localConfig.timezone}
              onChange={(e) => updateConfig({ timezone: e.target.value })}
              className="block w-full border border-white px-3 py-2 bg-black text-white focus:ring-purple-500 focus:border-purple-500 text-sm"
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Schedule Preview */}
          <div className="mt-4 p-4 bg-black border border-white">
            <h5 className="text-sm font-medium text-white mb-2">
              REVIEW
            </h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Recipients:</span>
                <span className="text-white font-medium">2 contacts</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Frequency:</span>
                <span className="text-white font-medium">{localConfig.frequency.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Send Date:</span>
                <span className="text-white font-medium">
                  {selectedDate ? selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Not set"}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-700">
              <p className="text-xs text-gray-300">
                {(() => {
                  const [, hour, , , dayOfWeek] = localConfig.cron.split(" ");
                  const time12 = new Date(
                    `2000-01-01T${localConfig.time}`
                  ).toLocaleTimeString("en-US", {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  });

                  // Add timezone to the schedule preview
                  const timezoneLabel =
                    timezones.find((tz) => tz.value === localConfig.timezone)
                      ?.label || localConfig.timezone;

                  switch (localConfig.frequency) {
                    case "daily":
                      if (localConfig.days.length === 7) {
                        return `Every day at ${time12} - ${timezoneLabel}`;
                      } else if (localConfig.days.length === 0) {
                        return `Every day at ${time12} - ${timezoneLabel}`;
                      } else {
                        const dayNames = localConfig.days
                          .map(
                            (d) =>
                              daysOfWeek.find((dow) => dow.value === d)?.label
                          )
                          .join(", ");
                        return `Every ${dayNames} at ${time12} - ${timezoneLabel}`;
                      }
                    case "weekly": {
                      const dayNames = localConfig.days
                        .map(
                          (d) => daysOfWeek.find((dow) => dow.value === d)?.label
                        )
                        .join(", ");
                      return `Every ${dayNames} at ${time12} ${timezoneLabel}`;
                    }
                    case "monthly": {
                      const dayOfMonth = localConfig.days[0] || "1";
                      return `Monthly on the ${dayOfMonth}${
                        dayOfMonth === "1"
                          ? "st"
                          : dayOfMonth === "2"
                          ? "nd"
                          : dayOfMonth === "3"
                          ? "rd"
                          : "th"
                      } at ${time12} - ${timezoneLabel}`;
                    }
                    case "quarterly": {
                      const qDayOfMonth = localConfig.days[0] || "1";
                      return `Quarterly on the ${qDayOfMonth}${
                        qDayOfMonth === "1"
                          ? "st"
                          : qDayOfMonth === "2"
                          ? "nd"
                          : qDayOfMonth === "3"
                          ? "rd"
                          : "th"
                      } at ${time12} - ${timezoneLabel}`;
                    }
                    default:
                      return "Custom schedule";
                  }
                })()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleSelector;
